<template>
    <Component :is="linkUrl ? NuxtLink : 'div'" v-slot="{href, navigate} = {}" :to="linkUrl" external custom>
        <div
            :class="['slice-testimonials--grid__quote-card', 'mui-background-lighter', {'slice-testimonials--grid__quote-card--has-link': linkUrl}]"
            @click="linkUrl ? navigate() : undefined"
        >
            <svg
                class="slice-testimonials--grid__quote-card-svg"
                width="41"
                height="32"
                viewBox="0 0 41 32"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M18.9384 0.550726L17.5611 4.9123C10.6744 2.96107 4.70591 8.92955 4.82069 17.1936C11.2483 14.2094 16.8724 18.4562 16.8724 23.9655C16.8724 28.6714 13.4291 32 8.95271 32C3.67291 32 0 27.6384 0 18.9153C0 5.60097 8.03449 -2.20395 18.9384 0.550726ZM40.2872 0.550726L39.0246 4.9123C32.0232 2.96107 26.1695 8.92955 26.1695 17.1936C32.7118 14.2094 38.2212 18.4562 38.2212 23.9655C38.2212 28.6714 34.8926 32 30.3015 32C25.1365 32 21.4636 27.6384 21.4636 18.9153C21.4636 5.60097 29.3833 -2.20395 40.2872 0.550726Z"
                    fill="var(--joy-color-neutral-70)"
                />
            </svg>
            <blockquote class="quote-card__text">
                <a v-if="linkUrl" :href="href || undefined">
                    <PrismicRichText
                        v-if="isFilled.richText(item.text)"
                        :field="item.text"
                        class="slice-testimonials--grid__quote-card-text"
                        data-testid="testimonials-grid-quote-card-text"
                    />
                </a>
                <PrismicRichText
                    v-else-if="isFilled.richText(item.text)"
                    :field="item.text"
                    class="slice-testimonials--grid__quote-card-text"
                    data-testid="testimonials-grid-quote-card-text"
                />
            </blockquote>

            <footer class="slice-testimonials--grid__quote-card-footer">
                <div class="slice-testimonials--grid__quote-card-author">
                    <div
                        v-if="item.authorName"
                        class="slice-testimonials--grid__quote-card-author-name"
                        data-testid="testimonials-grid-quote-card-author"
                    >
                        {{ item.authorName }}
                    </div>
                    <div
                        v-if="item.authorJobTitle"
                        class="slice-testimonials--grid__quote-card-author-job"
                        data-testid="testimonials-grid-quote-card-author-job"
                    >
                        {{ item.authorJobTitle }}
                    </div>
                </div>

                <div v-if="isFilled.image(item.logo)" class="slice-testimonials--grid__quote-card-separator" aria-hidden="true" />
                <MktMaltPrismicImage
                    v-if="isFilled.image(item.logo)"
                    :image-field="item.logo"
                    class="slice-testimonials--grid__quote-card-logo"
                    data-testid="testimonials-grid-quote-card-logo"
                    width="115"
                />
            </footer>
        </div>
    </Component>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {isFilled} from '@prismicio/helpers';
    import {PrismicRichText} from '@prismicio/vue';
    import type {TestimonialsSliceTestimonialsGridPrimaryItemsItem} from '@prismicio-types';
    import {MktMaltPrismicImage, NuxtLink} from '#components';

    const props = defineProps<{
        item: TestimonialsSliceTestimonialsGridPrimaryItemsItem;
    }>();

    const linkUrl = computed(() => (isFilled.link(props.item.link) && props.item.link.url ? props.item.link.url : undefined));
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;

    .slice-testimonials--grid__quote-card {
        display: flex;
        flex-direction: column;
        height: 100%;
        min-height: 220px;
        padding: var(--joy-core-spacing-8);
        border-radius: var(--joy-core-radius-4);
        text-align: left;
        transform-origin: center;
        will-change: transform;

        a {
            text-decoration: none;
            color: inherit;
        }

        &-svg {
            margin-bottom: var(--joy-core-spacing-4);
        }

        &-text {
            @include text.getFontBySize(primary-600);

            margin-bottom: var(--joy-core-spacing-6);
            color: var(--joy-color-neutral-60);

            p {
                margin: 0;
                font-weight: var(--joy-font-weight-normal);
                line-height: var(--joy-line-height-large);
            }
        }

        &-footer {
            display: flex;
            align-items: center;
            gap: var(--joy-core-spacing-3);
            margin-top: auto;
        }

        &-author {
            display: flex;
            flex-direction: column;
        }

        &-author-name {
            @include text.getFontBySize(primary-400);

            color: var(--joy-color-neutral-70);
            font-weight: var(--joy-font-weight-bold);
        }

        &-author-job {
            @include text.getFontBySize(primary-300);

            color: var(--joy-color-neutral-40);
            font-weight: var(--joy-font-weight-normal);
        }

        &-separator {
            flex: none;
            align-self: stretch;
            width: 1px;
            min-height: 40px;
            background-color: var(--joy-color-neutral-70);
        }

        &-logo {
            flex: none;
            width: auto;
            max-width: 115px;
            max-height: 29px;
            object-fit: contain;
        }
    }
</style>
